import * as React from 'react';
import {classify} from 'src/utils/classify';

import {Clickable} from 'src/designSystem2021Components/text-v2';

import {LabeledCheckbox} from 'src/designSystem2021Components/checkbox';
import {Radio} from 'src/designSystem2021Components/radio/radio';
import {Link} from 'src/rerouter/index';

import css from './options.css';


type OptionsClassNames = Readonly<{
  options?: string | null | undefined;
  down?: string | null | undefined;
  up?: string | null | undefined;
  showAll?: string | null | undefined;
  option?: string | null | undefined;
}>;

type OptionsDirection = 'up' | 'down';

export type OptionProps<V> = {
  className?: string | null | undefined;
  option: V;
  options: V[];
  isSelected: boolean;
  onSelect: (arg1: V) => unknown;
  resolveLabel: (arg1: V) => string | React.ReactNode;
  resolveValue: (arg1: V) => string;
  resolveIcon?: (arg1: V) => React.ReactNode;
  disabled?: boolean;
};

type CommonOptionsProps<V> = {
  value?: string[] | string;
  baseClassNames?: OptionsClassNames;
  classNames?: OptionsClassNames;
  onSelect: (arg1: V) => unknown;
  resolveValue: (arg1: V) => string;
  resolveLabel: (arg1: V) => string | React.ReactNode;
  resolveIcon?: (arg1: V) => React.ReactNode;
  resolveDisabled?: (arg1: V, arg2: V[]) => boolean;
};

export function Options<V>({
  options,
  baseClassNames = css,
  classNames,
  direction = 'down',
  showAll,
  OptionComponent = DefaultOption,
  value,
  onSelect,
  resolveValue,
  resolveLabel,
  resolveIcon,
  resolveDisabled = (_: V, __) => false,
  shadow = true,
}: CommonOptionsProps<V> & {
  OptionComponent?: React.ComponentType<OptionProps<V>>;
  options: V[];
  direction?: OptionsDirection;
  showAll?: unknown;
  shadow?: boolean;
}) {
  const normalizedValue =
    value != null ? (Array.isArray(value) ? value : [value]) : [];
  return (
    <div
      className={classify(
        baseClassNames.options,
        baseClassNames[direction],
        classNames?.options,
        showAll && baseClassNames.showAll,
        {[css.shadow]: shadow},
      )}
    >
      {options.map((option) => {
        const isSelected = normalizedValue.includes(resolveValue(option));
        return (
          <OptionComponent
            key={resolveValue(option)}
            className={classNames?.option}
            onSelect={onSelect}
            options={options}
            option={option}
            isSelected={isSelected}
            resolveLabel={resolveLabel}
            resolveValue={resolveValue}
            resolveIcon={resolveIcon}
            disabled={resolveDisabled(option, options)}
          />
        );
      })}
    </div>
  );
}

export const DefaultOption = <V extends unknown>({
  option,
  isSelected,
  resolveLabel,
  resolveIcon,
  onSelect,
  className,
  disabled = false,
}: OptionProps<V>) => {
  const label = resolveLabel(option);
  const icon = resolveIcon ? resolveIcon(option) : null;
  const handleSelect = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
    (_) => {
      onSelect(option);
    },
    [option, onSelect],
  );
  return (
    <div
      className={classify(css.option, className, {
        [css.selected]: isSelected,
        [css.disabled]: disabled,
      })}
      // @ts-expect-error - TS2322 - Type '((_: any) => void) | null' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
      onClick={disabled ? null : handleSelect}
    >
      {Boolean(icon) && <div>{icon}</div>}
      <Clickable>{label}</Clickable>
    </div>
  );
};

export const CheckboxOption = <V extends unknown>({
  option,
  isSelected,
  resolveLabel,
  onSelect,
  className,
  disabled = false,
}: OptionProps<V>) => {
  const label = resolveLabel(option);
  const handleChange = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
    (_) => {
      onSelect(option);
    },
    [option, onSelect],
  );
  return (
    <>
      {label ? (
        <LabeledCheckbox
          onChange={handleChange}
          checked={isSelected ? 'true' : 'false'}
          className={classify(className)}
          disabled={disabled}
        >
          {label}
        </LabeledCheckbox>
      ) : null}
    </>
  );
};

export const LinkOption = <V extends unknown>({
  option,
  className,
  resolveLabel,
  resolveValue,
  resolveIcon,
  disabled = false,
}: OptionProps<V>) => {
  const label = resolveLabel(option);
  const value = resolveValue(option);
  const icon = resolveIcon ? resolveIcon(option) : null;

  return (
    <Link
      className={classify(css.option, className, {[css.disabled]: disabled})}
      to={value}
    >
      {' '}
      {Boolean(icon) && <div>{icon}</div>} <Clickable>{label}</Clickable>
    </Link>
  );
};

export const RadioOption = <V extends unknown>({
  option,
  isSelected,
  resolveLabel,
  resolveValue,
  onSelect,
  className,
  disabled = false,
}: OptionProps<V>) => {
  const label = resolveLabel(option);
  const handleChange = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
    (_) => {
      onSelect(option);
    },
    [option, onSelect],
  );
  return (
    <div
      className={classify(css.option, css.radioOption, className, {
        [css.selected]: isSelected,
      })}
    >
      <Radio
        value={resolveValue(option)}
        checked={isSelected ? true : false}
        onChange={handleChange}
        disabled={disabled}
      >
        {label}
      </Radio>
    </div>
  );
};
